import { Badge, Col, Row, Table } from "reactstrap"

import ThemeModal from "components/Common/ThemeModal"
import { formatMinutes } from "utils/formatDate"

const formatTime = (dateTime, timeZone) => {
  if (!dateTime) return "—"
  const date = new Date(
    typeof dateTime === "string" ? dateTime.replace(" ", "T") : dateTime
  )
  if (isNaN(date.getTime())) return "—"
  const options = { hour: "2-digit", minute: "2-digit", second: "2-digit" }
  if (timeZone) options.timeZone = timeZone
  return date.toLocaleTimeString([], options)
}

const formatDateTime = (dateTime, timeZone) => {
  if (!dateTime) return "—"
  const date = new Date(
    typeof dateTime === "string" ? dateTime.replace(" ", "T") : dateTime
  )
  if (isNaN(date.getTime())) return "—"
  const options = {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }
  if (timeZone) options.timeZone = timeZone
  return date.toLocaleString([], options)
}

const formatDate = dateStr => {
  if (!dateStr) return "—"
  return new Date(`${dateStr.slice(0, 10)}T00:00:00`).toLocaleDateString([], {
    year: "numeric",
    month: "short",
    day: "2-digit",
    weekday: "short",
  })
}

const formatCoord = (lat, lng) => {
  if (lat == null || lng == null) return "—"
  return `${Number(lat).toFixed(7)}, ${Number(lng).toFixed(7)}`
}

const MapLink = ({ lat, lng, label }) => {
  if (lat == null || lng == null) return "—"
  const latN = Number(lat)
  const lngN = Number(lng)
  if (!Number.isFinite(latN) || !Number.isFinite(lngN)) return "—"
  const href = `https://www.google.com/maps?q=${latN},${lngN}`
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="d-inline-flex align-items-center gap-2 text-decoration-none"
      title="Open in Google Maps"
    >
      <span className="font-monospace small">{label}</span>
      <i className="mdi mdi-map-marker-outline text-primary" />
      <i className="mdi mdi-open-in-new text-muted small" />
    </a>
  )
}

const DetailRow = ({ label, value, mono = false }) => (
  <tr>
    <th
      scope="row"
      className="text-muted fw-medium"
      style={{ width: "40%", verticalAlign: "top" }}
    >
      {label}
    </th>
    <td
      className={mono ? "font-monospace small" : ""}
      style={{ wordBreak: "break-word" }}
    >
      {value || "—"}
    </td>
  </tr>
)

const SectionHeader = ({ children }) => (
  <tr>
    <td
      colSpan={2}
      className="bg-light text-uppercase text-muted fw-semibold small py-2"
    >
      {children}
    </td>
  </tr>
)

const StatusBadge = ({ record }) => {
  if (!record) return "—"
  if (record.status === "late") {
    return <Badge color="danger">Late</Badge>
  }
  if (record.status === "present") {
    return <Badge color="success">On time</Badge>
  }
  return <Badge color="secondary">—</Badge>
}

const AttendanceDetailsModal = ({
  isOpen,
  toggle,
  record,
  timeZone,
}) => {
  if (!record) {
    return <ThemeModal isOpen={isOpen} toggle={toggle} title="Attendance Details" />
  }

  return (
    <ThemeModal
      isOpen={isOpen}
      toggle={toggle}
      title="Attendance Details"
      size="lg"
    >
      <Row className="mb-3 g-2">
        <Col md={6}>
          <div className="text-muted small">Employee</div>
          <div className="fw-semibold">
            {record.employee?.name || "—"}
          </div>
          <div className="text-muted small">
            {record.employee?.employee_id || ""}
          </div>
        </Col>
        <Col md={3}>
          <div className="text-muted small">Date</div>
          <div className="fw-semibold">{formatDate(record.date)}</div>
        </Col>
        <Col md={3} className="text-md-end">
          <div className="text-muted small">Status</div>
          <div>
            <StatusBadge record={record} />
            {record.is_manual && (
              <Badge color="info" className="ms-1">
                Manual
              </Badge>
            )}
          </div>
          {record.status === "late" && record.late_minutes > 0 && (
            <div className="text-danger small mt-1">
              Late by {formatMinutes(record.late_minutes)}
            </div>
          )}
        </Col>
      </Row>

      <Table size="sm" className="mb-0" responsive borderless>
        <tbody>
          <SectionHeader>Check-in</SectionHeader>
          <DetailRow
            label="Time"
            value={formatDateTime(record.check_in_at, timeZone)}
          />
          <DetailRow
            label="Source"
            value={
              <Badge color="light" className="text-dark">
                {record.check_in_source || "—"}
              </Badge>
            }
          />
          <DetailRow
            label="Coordinates"
            value={
              <MapLink
                lat={record.check_in_lat}
                lng={record.check_in_lng}
                label={formatCoord(record.check_in_lat, record.check_in_lng)}
              />
            }
          />
          <DetailRow
            label="Distance from office"
            value={
              record.check_in_distance_m != null
                ? `${record.check_in_distance_m} m`
                : "—"
            }
          />

          <SectionHeader>Check-out</SectionHeader>
          <DetailRow
            label="Time"
            value={formatDateTime(record.check_out_at, timeZone)}
          />
          <DetailRow
            label="Source"
            value={
              record.check_out_source ? (
                <Badge color="light" className="text-dark">
                  {record.check_out_source}
                </Badge>
              ) : (
                "—"
              )
            }
          />
          <DetailRow
            label="Coordinates"
            value={
              <MapLink
                lat={record.check_out_lat}
                lng={record.check_out_lng}
                label={formatCoord(record.check_out_lat, record.check_out_lng)}
              />
            }
          />
          <DetailRow
            label="Distance from office"
            value={
              record.check_out_distance_m != null
                ? `${record.check_out_distance_m} m`
                : "—"
            }
          />
          <DetailRow
            label="Early leave"
            value={
              record.early_leave_minutes
                ? formatMinutes(record.early_leave_minutes)
                : "—"
            }
          />

          {(record.is_manual || record.edited_by || record.edit_reason) && (
            <>
              <SectionHeader>Audit</SectionHeader>
              <DetailRow
                label="Manual entry"
                value={record.is_manual ? "Yes" : "No"}
              />
              {record.edited_by && (
                <DetailRow
                  label="Edited by"
                  value={
                    typeof record.edited_by === "object"
                      ? record.edited_by?.name
                      : record.editor?.name || `User #${record.edited_by}`
                  }
                />
              )}
              {record.edited_at && (
                <DetailRow
                  label="Edited at"
                  value={formatDateTime(record.edited_at, timeZone)}
                />
              )}
              {record.edit_reason && (
                <DetailRow label="Edit reason" value={record.edit_reason} />
              )}
            </>
          )}

          {record.remarks && (
            <>
              <SectionHeader>Notes</SectionHeader>
              <DetailRow label="Remarks" value={record.remarks} />
            </>
          )}

          <SectionHeader>System</SectionHeader>
          <DetailRow label="Record ID" value={`#${record.id}`} mono />
          {record.showroom_id && (
            <DetailRow label="Showroom ID" value={record.showroom_id} mono />
          )}
          <DetailRow
            label="Timezone"
            value={
              <span>
                {timeZone || "(browser local)"}
                {timeZone && (
                  <span className="text-muted ms-2 small">
                    ({formatTime(record.check_in_at, timeZone)} in office)
                  </span>
                )}
              </span>
            }
          />
        </tbody>
      </Table>
    </ThemeModal>
  )
}

export default AttendanceDetailsModal
